import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FactoryIcon from '@mui/icons-material/Factory';
import LanguageIcon from '@mui/icons-material/Language';
import LayersIcon from '@mui/icons-material/Layers';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublicIcon from '@mui/icons-material/Public';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  ChoiceOption,
  COUNTRIES,
  CountryOption,
  countryName,
  INDUSTRIES,
  LanguageOption,
  languagesForCountries,
  NICHES,
  TASKS,
  TTS_LANGUAGES,
  TTS_MODELS,
  TTS_PROVIDERS,
  VOICE_STYLES,
  VOICES,
  withCurrentOption,
} from './aiAssistantCatalog';
import {
  AssistantFormState,
  assistantStatusFromForm,
  buildVoiceLanguageSettings,
  emptyAssistantForm,
  firstInvalidStep,
  formFromConfig,
  stepError,
} from './aiAssistantForm';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { httpService } from '../../services/httpService';
import {
  getVoiceAssistantApiError,
  VoiceLanguageSettings,
  voiceAssistantService,
} from '../../services/voiceAssistantService';

interface AIAssistantSectionProps {
  organizationId: string;
  applicationId: string;
  botId: string;
  botName: string;
}

type PageKey = 'task' | 'industry' | 'niche';

const controlSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: '#fff',
    minHeight: 56,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5eaf1',
  },
};

const fieldLabelSx = {
  fontWeight: 700,
  color: '#172033',
  mb: 1,
};

const choiceCardSx = (selected: boolean) => ({
  borderRadius: 3,
  border: '1.5px solid',
  borderColor: selected ? '#007aff' : '#e6ebf2',
  backgroundColor: selected ? '#f4f8ff' : '#fff',
  minHeight: 112,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.25,
  px: 2,
  py: 2,
  textAlign: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  boxShadow: selected ? '0 8px 20px rgba(0, 122, 255, 0.08)' : 'none',
  transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '#007aff',
    transform: 'translateY(-2px)',
  },
});

const STEPS = [
  {
    key: 'voice',
    title: 'Voice & Language Settings',
    subtitle: 'Configure how your AI Assistant will sound and communicate',
    icon: <LanguageIcon sx={{ color: '#16a34a', fontSize: 30 }} />,
  },
  {
    key: 'task',
    title: 'What should your AI Assistant do?',
    subtitle: 'Select the main task your AI will handle. This shapes how it responds and what skills it prioritizes.',
    icon: <WorkOutlineIcon sx={{ color: '#7c3aed', fontSize: 30 }} />,
  },
  {
    key: 'industry',
    title: 'What industry are you in?',
    subtitle: 'Your AI will use industry-specific language, understand common terms, and follow your industry best practices.',
    icon: <FactoryIcon sx={{ color: '#16a34a', fontSize: 30 }} />,
  },
  {
    key: 'niche',
    title: 'Specify your niche',
    subtitle: '',
    icon: <LayersIcon sx={{ color: '#f97316', fontSize: 30 }} />,
  },
  {
    key: 'review',
    title: 'Review and save',
    subtitle: 'Confirm the assistant for this bot. Saving creates the configuration, or updates it when one already exists.',
    icon: <TaskAltIcon sx={{ color: '#007aff', fontSize: 30 }} />,
  },
];

const ChoiceCard: React.FC<{
  label: string;
  icon?: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ label, icon, selected, onSelect }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
    sx={choiceCardSx(selected)}
  >
    {icon && <Box sx={{ fontSize: 34, lineHeight: 1 }}>{icon}</Box>}
    <Typography sx={{ fontWeight: 700, color: '#172033', lineHeight: 1.35 }}>{label}</Typography>
  </Box>
);

const PagedChoices: React.FC<{
  items: ChoiceOption[];
  page: number;
  pageSize: number;
  columns: 2 | 3;
  accent: string;
  selected: string;
  onPage: (page: number) => void;
  onSelect: (label: string) => void;
}> = ({ items, page, pageSize, columns, accent, selected, onPage, onSelect }) => {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visible = items.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <Box sx={{ position: 'relative', px: { xs: 0, sm: 5 } }}>
      <IconButton
        aria-label="Previous options"
        disabled={safePage === 0}
        onClick={() => onPage(safePage - 1)}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          position: 'absolute',
          left: 0,
          top: '42%',
          bgcolor: '#fff',
          border: '1px solid #e8eef5',
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: columns === 2
            ? { xs: '1fr', sm: '1fr 1fr' }
            : { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        {visible.map((item) => (
          <ChoiceCard
            key={item.label}
            label={item.label}
            icon={item.icon}
            selected={selected === item.label}
            onSelect={() => onSelect(item.label)}
          />
        ))}
      </Box>
      <IconButton
        aria-label="Next options"
        disabled={safePage >= pageCount - 1}
        onClick={() => onPage(safePage + 1)}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          position: 'absolute',
          right: 0,
          top: '42%',
          bgcolor: '#fff',
          border: '1px solid #e8eef5',
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
        }}
      >
        <ChevronRightIcon />
      </IconButton>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2.5 }}>
          {Array.from({ length: pageCount }).map((_, index) => (
            <Box
              key={index}
              role="button"
              aria-label={`Page ${index + 1}`}
              onClick={() => onPage(index)}
              sx={{
                width: index === safePage ? 22 : 8,
                height: 8,
                borderRadius: 99,
                cursor: 'pointer',
                backgroundColor: index === safePage ? accent : '#d5dbe3',
              }}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'space-between', mt: 2 }}>
        <Button startIcon={<ChevronLeftIcon />} disabled={safePage === 0} onClick={() => onPage(safePage - 1)}>
          Previous
        </Button>
        <Button endIcon={<ChevronRightIcon />} disabled={safePage >= pageCount - 1} onClick={() => onPage(safePage + 1)}>
          Next
        </Button>
      </Box>
    </Box>
  );
};

const SelectField: React.FC<{
  label?: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  renderLabel?: (value: string) => string;
}> = ({ label, value, options, onChange, renderLabel }) => (
  <Box>
    {label && (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mb: 0.75 }}>
        {label}
      </Typography>
    )}
    <TextField
      select
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={controlSx}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {renderLabel ? renderLabel(option) : option}
        </MenuItem>
      ))}
    </TextField>
  </Box>
);

const AIAssistantSection: React.FC<AIAssistantSectionProps> = ({
  organizationId,
  applicationId,
  botId,
  botName,
}) => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const ctx = useMemo(() => ({
    organization_id: organizationId,
    application_id: applicationId,
    bot_id: botId,
  }), [organizationId, applicationId, botId]);

  const [step, setStep] = useState(0);
  const [pages, setPages] = useState<Record<PageKey, number>>({ task: 0, industry: 0, niche: 0 });
  const [form, setForm] = useState<AssistantFormState>(emptyAssistantForm);
  const [savedSettings, setSavedSettings] = useState<VoiceLanguageSettings | undefined>(undefined);
  const [hasConfig, setHasConfig] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!ctx.organization_id || !ctx.application_id || !ctx.bot_id) {
        setIsLoading(false);
        setLoadError('Organization or application details are missing.');
        return;
      }
      setIsLoading(true);
      setLoadError('');
      setStep(0);
      try {
        const result = await voiceAssistantService.get(ctx);
        if (cancelled) return;
        if (result.exists && result.voice_assistant) {
          setHasConfig(true);
          setSavedSettings(result.voice_assistant.voice_language_settings);
          setForm(formFromConfig(result.voice_assistant));
        } else {
          setHasConfig(false);
          setSavedSettings(undefined);
          setForm(emptyAssistantForm());
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(getVoiceAssistantApiError(error, 'Failed to load the AI assistant.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [ctx, reloadKey]);

  const languageOptions = useMemo(() => {
    const base = languagesForCountries(form.countries);
    const extras: LanguageOption[] = form.languages
      .filter((code) => !base.some((language) => language.code === code))
      .map((code) => ({
        code,
        label: code,
        badge: code.slice(0, 2).toLowerCase(),
        countries: [],
      }));
    return [...extras, ...base];
  }, [form.countries, form.languages]);

  const countryOptions = useMemo(() => {
    const extras: CountryOption[] = form.countries
      .filter((code) => !COUNTRIES.some((country) => country.code === code))
      .map((code) => ({ code, name: code }));
    return [...extras, ...COUNTRIES];
  }, [form.countries]);

  const tasks = useMemo(() => (
    form.task && !TASKS.some((item) => item.label === form.task)
      ? [{ label: form.task, icon: '✨' }, ...TASKS]
      : TASKS
  ), [form.task]);

  const industries = useMemo(() => (
    form.industry && !INDUSTRIES.some((item) => item.label === form.industry)
      ? [{ label: form.industry, icon: '🏢' }, ...INDUSTRIES]
      : INDUSTRIES
  ), [form.industry]);

  const niches = useMemo(() => {
    const base = NICHES[form.industry] || [];
    const labels = form.niche && !base.includes(form.niche) ? [form.niche, ...base] : base;
    return labels.map((label) => ({ label, icon: '' }));
  }, [form.industry, form.niche]);

  const voices = useMemo(() => {
    const matching = VOICES.filter((voice) => voice.gender === form.gender);
    if (!form.voice || matching.some((voice) => voice.id === form.voice)) return matching;
    return [{ id: form.voice, label: form.voice, gender: form.gender }, ...matching];
  }, [form.gender, form.voice]);

  const currentError = stepError(step, form);
  const stepMeta = STEPS[step];
  const canTestAssistant = hasConfig && form.status === 'Active' && !isTesting;
  const testDisabledReason = isTesting
    ? 'Opening AI Employee...'
    : hasConfig && form.status === 'Active'
      ? 'Test this assistant'
      : !hasConfig
        ? 'Save this assistant before testing.'
        : 'Set status to Active to test this assistant.';

  const handleTestAssistant = async () => {
    if (!hasConfig || form.status !== 'Active' || isTesting) return;

    const orgId = organizationId || userDetails?.organization_id || '';
    const appId = applicationId || '';
    const userId = userDetails?.user_id || '';
    if (!orgId || !appId || !userId || !botId) {
      toast.error('Organization, application, bot, or user details are missing.', { position: 'top-right' });
      return;
    }

    // Open during the click gesture so the browser does not treat it as a blocked popup
    // after the async credential request below.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      toast.error('Please allow pop-ups to open AI Employee in a new tab.', { position: 'top-right' });
      return;
    }

    setIsTesting(true);
    try {
      const payload = {
        request_id: 'ai-employee-test',
        timestamp: new Date().toISOString(),
        action: 'get_user_credentials',
        data: await encryptResponse({
          organization_id: orgId,
          app_id: appId,
          user_id: userId,
        }, REQ_ENC_KEY),
      };

      type ApiResponse = { status: number; data?: string; message?: string };
      const response = await httpService.post<ApiResponse>(`${API_BASE_URL}/user/getUserCredentials`, payload);
      if (response.data.status !== 200 || !response.data.data) {
        throw new Error(response.data.message || 'Could not load application credentials.');
      }

      const decrypted = await decryptResponse<{
        appId: string;
        secretKey: string;
      }>(response.data.data, getSessionKey() as string);

      const resolvedAppId = decrypted.appId || appId;
      const secretKey = decrypted.secretKey || '';
      if (!resolvedAppId || !secretKey) {
        throw new Error('Application credentials are incomplete.');
      }

      // Opaque query keys so the URL does not expose field names.
      const params = new URLSearchParams({
        a: resolvedAppId,
        k: secretKey,
        b: botId,
      });
      popup.location.href = `${window.location.origin}/ai-employee?${params.toString()}`;
      popup.opener = null;
    } catch (error) {
      popup.close();
      toast.error(
        error instanceof Error ? error.message : 'Could not open AI Employee.',
        { position: 'top-right' }
      );
    } finally {
      setIsTesting(false);
    }
  };

  const setGender = (gender: AssistantFormState['gender']) => {
    setForm((prev) => {
      const current = VOICES.find((voice) => voice.id === prev.voice);
      const nextVoice = current && current.gender !== gender
        ? (VOICES.find((voice) => voice.gender === gender)?.id || prev.voice)
        : prev.voice;
      return { ...prev, gender, voice: nextVoice };
    });
  };

  const handleSave = async () => {
    const invalid = firstInvalidStep(form);
    if (invalid) {
      setStep(invalid.step);
      toast.error(invalid.message, { position: 'top-right' });
      return;
    }
    setIsSaving(true);
    try {
      const settings = buildVoiceLanguageSettings(form, savedSettings);
      const { message, record } = await voiceAssistantService.save(
        ctx,
        settings,
        assistantStatusFromForm(form.status)
      );
      setHasConfig(true);
      setSavedSettings(record.voice_language_settings);
      setForm(formFromConfig(record));
      toast.success(message || 'AI Assistant saved.', { position: 'top-right' });
    } catch (error) {
      toast.error(getVoiceAssistantApiError(error, 'Failed to save the AI assistant.'), { position: 'top-right' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = () => {
    if (currentError) {
      toast.error(currentError, { position: 'top-right' });
      return;
    }
    if (step < STEPS.length - 1) {
      setStep((prev) => prev + 1);
      return;
    }
    handleSave();
  };

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading AI assistant...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="error" sx={{ mb: 2 }}>{loadError}</Typography>
          <Button variant="outlined" onClick={() => setReloadKey((current) => current + 1)}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const summary: Array<[string, string]> = [
    ['Bot', botName],
    ['Gender', form.gender === 'male' ? 'Male' : 'Female'],
    ['Countries', form.countries.length ? form.countries.map(countryName).join(', ') : 'All countries'],
    ['Languages', form.languages.join(', ')],
    ['TTS provider', form.provider],
    ['Model', form.model],
    ['TTS language', form.ttsLanguage],
    ['Voice', `${form.voice} — ${form.gender}`],
    ['Speed', `${Number(form.speed).toFixed(1)}x`],
    ['Voice style', form.voiceStyle],
    ['Task', form.task],
    ['Industry', form.industry],
    ['Niche', form.niche],
  ];

  return (
    <Card sx={{ borderRadius: 4, border: '1px solid #e8eef5', boxShadow: '0 12px 40px rgba(15, 23, 42, 0.06)' }}>
      <CardContent sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3.5 } }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, mb: 1.5 }}>
          Step {step + 1} of {STEPS.length}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
          {STEPS.map((item, index) => {
            const canOpen = index < step || (hasConfig && index !== step);
            return (
              <Box
                key={item.key}
                role="button"
                aria-label={`Step ${index + 1}: ${item.title}`}
                tabIndex={canOpen ? 0 : -1}
                onClick={() => canOpen && setStep(index)}
                onKeyDown={(event) => {
                  if (!canOpen) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setStep(index);
                  }
                }}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 99,
                  backgroundColor: index <= step ? '#007aff' : '#e7edf5',
                  cursor: canOpen ? 'pointer' : 'default',
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
              borderRadius: 3,
              border: '1px solid #e8eef6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
              backgroundColor: '#fff',
            }}
          >
            {stepMeta.icon}
          </Box>
          <Typography sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, color: '#172033', letterSpacing: -0.4 }}>
            {stepMeta.title}
          </Typography>
          <Typography sx={{ mt: 1.25, color: '#7b8798', maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
            {stepMeta.key === 'niche' ? (
              <>
                Select a sub-category for{' '}
                <Box component="span" sx={{ color: '#007aff', fontWeight: 700 }}>{form.industry || 'your industry'}</Box>
                {' '}to help your AI Assistant understand specific terminology and practices.
              </>
            ) : stepMeta.subtitle}
          </Typography>
        </Box>

        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography sx={fieldLabelSx}>Gender</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {(['female', 'male'] as const).map((gender) => {
                  const selected = form.gender === gender;
                  return (
                    <Button
                      key={gender}
                      fullWidth
                      onClick={() => setGender(gender)}
                      sx={{
                        py: 1.75,
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 16,
                        color: '#172033',
                        border: '1.5px solid',
                        borderColor: selected ? '#007aff' : '#e5eaf2',
                        backgroundColor: selected ? '#f3f8ff' : '#fff',
                        '&:hover': { borderColor: '#007aff', backgroundColor: selected ? '#f3f8ff' : '#fafcff' },
                      }}
                    >
                      {gender === 'female' ? 'Female' : 'Male'}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography sx={{ ...fieldLabelSx, mb: 0 }}>Multilingual Mode</Typography>
                <Chip
                  size="small"
                  icon={<StarRoundedIcon sx={{ fontSize: '16px !important' }} />}
                  label="Enterprise"
                  sx={{
                    backgroundColor: '#fff4d6',
                    color: '#b7791f',
                    fontWeight: 700,
                    '& .MuiChip-icon': { color: '#e0a100' },
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
                Contact support to enable Multilingual Mode for your account.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e8eef5',
                  backgroundColor: '#f8fafc',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PublicIcon sx={{ color: '#93c5fd' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#94a3b8' }}>Enable Multilingual</Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Auto-detect & respond in any language
                    </Typography>
                  </Box>
                </Box>
                <Switch disabled checked={form.multilingualEnabled} />
              </Box>
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>
                Target Countries{' '}
                <Typography component="span" variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  (filters languages below)
                </Typography>
              </Typography>
              <Autocomplete
                multiple
                options={countryOptions}
                value={countryOptions.filter((country) => form.countries.includes(country.code))}
                onChange={(_, value) => {
                  const nextCountries = value.map((country) => country.code);
                  setForm((prev) => {
                    const allowed = new Set(languagesForCountries(nextCountries).map((language) => language.code));
                    return {
                      ...prev,
                      countries: nextCountries,
                      languages: nextCountries.length
                        ? prev.languages.filter((code) => allowed.has(code))
                        : prev.languages,
                    };
                  });
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={form.countries.length ? '' : 'Select countries...'}
                    sx={controlSx}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>
                Language <Box component="span" sx={{ color: '#e11d48' }}>*</Box>
              </Typography>
              <Autocomplete
                multiple
                options={languageOptions}
                value={languageOptions.filter((language) => form.languages.includes(language.code))}
                onChange={(_, value) => setForm((prev) => ({ ...prev, languages: value.map((language) => language.code) }))}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Box component="span" sx={{ mr: 1, color: '#007aff', fontWeight: 700, textTransform: 'lowercase' }}>
                        {option.badge}
                      </Box>
                      {option.label}
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) => value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      label={`${option.badge} ${option.label}`}
                      sx={{ backgroundColor: '#e8f1ff', color: '#1d4ed8', fontWeight: 600 }}
                    />
                  );
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={form.languages.length ? '' : 'Select languages...'}
                    sx={controlSx}
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: '1px solid #e8eef5',
                backgroundColor: '#fbfcfe',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                <AutoAwesomeIcon sx={{ color: '#a855f7', fontSize: 18 }} />
                <Typography sx={{ fontWeight: 800, color: '#172033' }}>
                  Voice <Box component="span" sx={{ color: '#e11d48' }}>*</Box>
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2.5 }}>
                <SelectField
                  label="TTS Provider"
                  value={form.provider}
                  options={withCurrentOption(TTS_PROVIDERS, form.provider)}
                  onChange={(provider) => setForm((prev) => ({ ...prev, provider }))}
                />
                <SelectField
                  label="Model"
                  value={form.model}
                  options={withCurrentOption(TTS_MODELS, form.model)}
                  onChange={(model) => setForm((prev) => ({ ...prev, model }))}
                />
                <SelectField
                  label="TTS Language"
                  value={form.ttsLanguage}
                  options={withCurrentOption(TTS_LANGUAGES, form.ttsLanguage)}
                  onChange={(ttsLanguage) => setForm((prev) => ({ ...prev, ttsLanguage }))}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mb: 0.75 }}>
                Voice
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                <TextField
                  select
                  fullWidth
                  value={form.voice}
                  onChange={(event) => setForm((prev) => ({ ...prev, voice: event.target.value }))}
                  sx={{ ...controlSx, flex: 1, minWidth: 0 }}
                >
                  {voices.map((voice) => (
                    <MenuItem key={voice.id} value={voice.id}>
                      {voice.label} — {voice.gender}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title={testDisabledReason}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={isTesting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                      disabled={!canTestAssistant}
                      onClick={handleTestAssistant}
                      sx={{
                        backgroundColor: '#1f2a37',
                        minWidth: 112,
                        height: 56,
                        borderRadius: '14px',
                        textTransform: 'none',
                        fontWeight: 700,
                        flexShrink: 0,
                        '&:hover': { backgroundColor: '#111827' },
                        '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                      }}
                    >
                      {isTesting ? 'Opening...' : 'Test'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, color: '#172033' }}>Speed</Typography>
                <Typography sx={{ fontWeight: 700, color: '#007aff' }}>{Number(form.speed).toFixed(1)}x</Typography>
              </Box>
              <Slider
                aria-label="Voice speed"
                value={form.speed}
                min={0.5}
                max={2}
                step={0.1}
                onChange={(_, value) => {
                  const next = Array.isArray(value) ? value[0] : value;
                  setForm((prev) => ({ ...prev, speed: next }));
                }}
                sx={{
                  color: '#007aff',
                  height: 6,
                  '& .MuiSlider-thumb': { width: 18, height: 18 },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                <Typography variant="caption">Slower (0.5x)</Typography>
                <Typography variant="caption">Normal (1.0x)</Typography>
                <Typography variant="caption">Faster (2.0x)</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                Choose a voice that matches your brand personality
              </Typography>
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>Voice Style</Typography>
              <SelectField
                value={form.voiceStyle}
                options={withCurrentOption(VOICE_STYLES.map((style) => style.label), form.voiceStyle)}
                onChange={(voiceStyle) => setForm((prev) => ({ ...prev, voiceStyle }))}
              />
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                Set the personality tone for your AI assistant
              </Typography>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <PagedChoices
            items={tasks}
            page={pages.task}
            pageSize={2}
            columns={2}
            accent="#007aff"
            selected={form.task}
            onPage={(task) => setPages((prev) => ({ ...prev, task }))}
            onSelect={(task) => setForm((prev) => ({ ...prev, task }))}
          />
        )}

        {step === 2 && (
          <PagedChoices
            items={industries}
            page={pages.industry}
            pageSize={6}
            columns={3}
            accent="#007aff"
            selected={form.industry}
            onPage={(industry) => setPages((prev) => ({ ...prev, industry }))}
            onSelect={(industry) => {
              setForm((prev) => ({
                ...prev,
                industry,
                niche: prev.industry === industry ? prev.niche : '',
              }));
              setPages((prev) => ({ ...prev, niche: 0 }));
            }}
          />
        )}

        {step === 3 && (
          <PagedChoices
            items={niches}
            page={pages.niche}
            pageSize={6}
            columns={3}
            accent="#f97316"
            selected={form.niche}
            onPage={(niche) => setPages((prev) => ({ ...prev, niche }))}
            onSelect={(niche) => setForm((prev) => ({ ...prev, niche }))}
          />
        )}

        {step === 4 && (
          <Box>
            <Box sx={{ border: '1px solid #e8eef5', borderRadius: 3, overflow: 'hidden', textAlign: 'left' }}>
              {summary.map(([label, value], index) => (
                <Box
                  key={label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    justifyItems: 'start',
                    columnGap: 3,
                    px: 2.5,
                    py: 1.5,
                    textAlign: 'left',
                    backgroundColor: index % 2 === 0 ? '#fbfcfe' : '#fff',
                    borderTop: index === 0 ? 'none' : '1px solid #eef2f7',
                  }}
                >
                  <Typography sx={{ color: '#64748b', fontWeight: 600, textAlign: 'left', width: '100%' }}>{label}</Typography>
                  <Typography sx={{ color: '#172033', fontWeight: 600, textAlign: 'left', width: '100%', overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ ...fieldLabelSx, mt: 3 }}>Status</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {([
                ['InActive', 'Inactive'],
                ['Active', 'Active'],
              ] as const).map(([value, label]) => {
                const selected = form.status === value;
                return (
                  <Button
                    key={value}
                    fullWidth
                    onClick={() => setForm((prev) => ({ ...prev, status: value }))}
                    sx={{
                      py: 1.75,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#172033',
                      border: '1.5px solid',
                      borderColor: selected ? '#007aff' : '#e5eaf2',
                      backgroundColor: selected ? '#f3f8ff' : '#fff',
                      '&:hover': { borderColor: '#007aff', backgroundColor: selected ? '#f3f8ff' : '#fafcff' },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 4,
            pt: 2.5,
            borderTop: '1px solid #eef2f7',
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            disabled={step === 0 || isSaving}
            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
            sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Back
          </Button>
          {currentError && (
            <Typography variant="body2" sx={{ color: '#b45309', textAlign: 'right', flex: 1 }}>
              {currentError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
            {step === STEPS.length - 1 && (
              <Tooltip title={testDisabledReason}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={isTesting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    disabled={!canTestAssistant}
                    onClick={handleTestAssistant}
                    sx={{
                      minWidth: 120,
                      py: 1.25,
                      px: 3,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      backgroundColor: '#1f2a37',
                      '&:hover': { backgroundColor: '#111827' },
                      '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                    }}
                  >
                    {isTesting ? 'Opening...' : 'Test'}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              variant="contained"
              disabled={Boolean(currentError) || isSaving}
              onClick={handleContinue}
              endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                minWidth: 150,
                py: 1.25,
                px: 3,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: '#1f2a37',
                '&:hover': { backgroundColor: '#111827' },
                '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
              }}
            >
              {isSaving ? 'Saving...' : step === STEPS.length - 1 ? 'Save' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AIAssistantSection;
